import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { LayoutDashboard, Dumbbell, TrendingUp, BookOpen, Shield, LogOut, Menu, X, Users2, Sparkles, UserCircle2, Download } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { NovatusLogo } from "./NovatusLogo";
import HelpButton from "./HelpButton";
import OnboardingModal from "./OnboardingModal";
import MinuteBalance from "./MinuteBalance";
import PaywallModal from "./PaywallModal";
import { api } from "../lib/api";

const BASE_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/training", label: "Training", icon: Dumbbell, testid: "nav-training" },
  { to: "/profile", label: "Profil", icon: UserCircle2, testid: "nav-profile" },
  { to: "/fortschritt", label: "Fortschritt", icon: TrendingUp, testid: "nav-progress" },
  { to: "/highlights", label: "Highlights", icon: Sparkles, testid: "nav-highlights" },
  { to: "/academy", label: "Academy", icon: BookOpen, testid: "nav-academy" },
];

const Layout = ({ children }) => {
  const { user, logout, refreshUser } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [paywallOpen, setPaywallOpen] = useState(false);
  const [paywallWallet, setPaywallWallet] = useState(null);

  // Listen for global "open paywall" events from any page (e.g. when a Voice
  // session fails to start with 402).
  useEffect(() => {
    const handler = (e) => {
      setPaywallWallet(e.detail?.wallet || null);
      setPaywallOpen(true);
    };
    window.addEventListener("open-paywall", handler);
    return () => window.removeEventListener("open-paywall", handler);
  }, []);

  const onTopUpFromBadge = async () => {
    try {
      const { data } = await api.get("/me/wallet");
      setPaywallWallet(data);
    } catch (e) { /* noop */ }
    setPaywallOpen(true);
  };

  // Auto-open onboarding tour for first-time users
  useEffect(() => {
    if (user && user.onboarding_completed !== true) {
      setShowOnboarding(true);
    }
  }, [user]);

  const completeOnboarding = async () => {
    try {
      await api.post("/me/onboarding/complete");
      setShowOnboarding(false);
      refreshUser?.();
    } catch (e) {
      console.warn("Failed to mark onboarding complete", e);
      setShowOnboarding(false);
    }
  };

  const skipOnboarding = async () => {
    // Treat skip the same as complete — user has seen it once.
    await completeOnboarding();
  };

  const reopenTour = () => setShowOnboarding(true);

  const isAdmin = ["admin", "trainer", "manager", "direktor"].includes(user?.role);
  const isStrictAdmin = user?.role === "admin";
  const navItems = isAdmin
    ? [
        ...BASE_NAV,
        { to: "/team", label: "Team", icon: Users2, testid: "nav-team" },
        { to: "/admin", label: "Admin", icon: Shield, testid: "nav-admin" },
        ...(isStrictAdmin
          ? [{ to: "/downloads", label: "Downloads", icon: Download, testid: "nav-downloads" }]
          : []),
      ]
    : BASE_NAV;

  const onLogout = () => {
    logout();
    navigate("/login");
  };

  const Tab = ({ item }) => {
    const Active = location.pathname.startsWith(item.to);
    const Icon = item.icon;
    return (
      <Link
        to={item.to}
        data-testid={item.testid}
        onClick={() => setMobileOpen(false)}
        className={`pill-tab ${Active ? "active" : ""}`}
      >
        <Icon className="w-4 h-4" />
        {item.label}
      </Link>
    );
  };

  return (
    <div className="min-h-screen bg-dotted">
      <header className="glass-header sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[68px] flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 shrink-0 min-w-0">
            <button
              className="lg:hidden p-2 rounded-md hover:bg-white/5"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
            <Link to="/dashboard" className="flex items-center gap-2 sm:gap-3 min-w-0" data-testid="brand-link">
              <NovatusLogo size={38} />
              <div className="flex flex-col leading-none min-w-0">
                <span className="font-display font-black text-sm sm:text-base tracking-tight truncate">
                  <span className="text-white">NOVATUS</span>
                  <span className="text-novatus-green"> Energie</span>
                </span>
                <span className="hidden sm:inline text-[10px] tracking-[0.28em] uppercase text-novatus-text-3 font-semibold mt-1 truncate">
                  SALES ACADEMY
                </span>
              </div>
            </Link>
          </div>

          <nav className="hidden lg:flex items-center gap-1 overflow-x-auto flex-1 min-w-0 justify-start">
            {navItems.map((item) => (<Tab key={item.to} item={item} />))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3 shrink-0">
            <MinuteBalance onTopUp={onTopUpFromBadge} />
            <HelpButton onReopenTour={reopenTour} />
            <div className="hidden 2xl:flex flex-col items-end leading-tight" data-testid="user-summary">
              <span className="text-sm font-semibold text-white">{user?.first_name} {user?.last_name}</span>
              <span className="text-[10px] tracking-[0.18em] uppercase text-novatus-green font-semibold">{user?.level || "Anfänger"}</span>
            </div>
            <button
              onClick={onLogout}
              data-testid="logout-btn"
              className="p-2 rounded-lg text-novatus-text-2 hover:text-white hover:bg-white/5 transition-colors"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="lg:hidden border-t border-novatus bg-novatus-bg">
            <div className="px-4 py-3 flex flex-wrap gap-2">
              {navItems.map((item) => (<Tab key={item.to} item={item} />))}
            </div>
            <button
              onClick={() => { setMobileOpen(false); onTopUpFromBadge(); }}
              className="w-full px-4 py-3 border-t border-novatus flex items-center justify-between text-left text-sm hover:bg-white/5"
              data-testid="mobile-wallet-row"
            >
              <span className="text-novatus-text-2">Voice-Minuten</span>
              <span className="text-novatus-green font-bold">Guthaben aufladen →</span>
            </button>
          </div>
        )}
      </header>

      <main>{children}</main>

      <footer className="border-t border-novatus mt-12 py-6" data-testid="app-footer">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-wrap items-center justify-between gap-3 text-xs text-novatus-text-3">
          <span>© {new Date().getFullYear()} NOVATUS Energie GmbH</span>
          <div className="flex gap-4 flex-wrap">
            <Link to="/impressum" className="hover:text-novatus-green transition-colors" data-testid="footer-impressum">Impressum</Link>
            <Link to="/datenschutz" className="hover:text-novatus-green transition-colors" data-testid="footer-datenschutz">Datenschutz</Link>
            <Link to="/agb" className="hover:text-novatus-green transition-colors" data-testid="footer-agb">AGB</Link>
            <Link to="/widerruf" className="hover:text-novatus-green transition-colors" data-testid="footer-widerruf">Widerruf</Link>
          </div>
        </div>
      </footer>

      <OnboardingModal
        open={showOnboarding}
        onClose={skipOnboarding}
        onComplete={completeOnboarding}
      />

      <PaywallModal
        open={paywallOpen}
        onClose={() => setPaywallOpen(false)}
        wallet={paywallWallet}
      />
    </div>
  );
};

export default Layout;
